import AmountReceived from '@components/amounts-received/amounts-received.component'
import AnticipationFlow from '@components/anticipation-flow/anticipation-flow.component'
import CardHelpLink from '@components/card-help-link/card-help-link.component'
import IncomeReport from '@components/income-report/income-report.component'
import { withTransaction } from '@elastic/apm-rum-react'
import { Button, Callout, FinancialOperationService, HistoryTransaction, Loader, NextPayments, onUserChange, User } from '@hub/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import './payments-info.page.scoped.scss'
//@ts-ignore
import { paymentHistory } from '@hub/shared/services/financial-operation/financial-operation.service'

interface Card {
  text: string
  link: string
  key: string | any
}

const PaymentsInfoPageComponent = () => {
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const financialOperationService = useMemo(() => new FinancialOperationService(), [])
  const [t] = useTranslation('paymentsInfo')
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [nextPage, setNextPage] = useState<number | null>(1)
  const [transactions, setTransactions] = useState<HistoryTransaction[]>([])
  const navigate = useNavigate()

  const cardsRPA: Array<Card> = useMemo(
    () => [
      {
        key: 'discountAndUpload',
        text: t('discountAndUpload.rpa.text'),
        link: 'https://meajuda.wakecreators.com/docs/descontos-de-impostos-em-pagamentos-via-rpa',
      },
      {
        key: 'advantageousAndFillNf',
        text: t('advantageousAndFillNf.rpa.text'),
        link: 'https://meajuda.wakecreators.com/docs/o-que-e-mais-vantajoso-cnpj-ou-rpa',
      },
    ],
    [t]
  )
  const cardsNF = useMemo(
    () => [
      {
        key: 'discountAndUpload',
        text: t('discountAndUpload.nf.text'),
        link: 'https://meajuda.wakecreators.com/v1/docs/pt-br/tenho-data-especifica-para-fazer-upload-da-minha-nf-1',
      },
      {
        key: 'advantageousAndFillNf',
        text: t('advantageousAndFillNf.nf.text'),
        link: 'https://meajuda.wakecreators.com/docs/como-preencher-a-minha-nf',
      },
    ],
    [t]
  )

  const getToReceiveHistory = useCallback(
    (updateList?: boolean) => {
      const page = updateList ? 1 : nextPage || 1
      const limit = updateList ? transactions?.length : 10
      financialOperationService
        //@ts-ignore
        .getPaymentHistory('toReceive', page, limit)
        .then((e: paymentHistory) => {
          const newArray = updateList ? e?.transactions : transactions.concat(e?.transactions)
          setTransactions(newArray)
          setNextPage(newArray?.length < e?.total && page ? page + 1 : null)
        })
        .catch(() => {
          setTransactions([])
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [nextPage, transactions, financialOperationService]
  )

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((user: User) => {
      setLoading(true)
      if (user?.squidId) {
        setUser(user)
        getToReceiveHistory()
      }
    })

    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUserChangeSubscription])

  const cardsHelpText = () => {
    const cardsList = user?.banks?.[0]?.paymentType === 'rpa' ? cardsRPA : cardsNF
    return cardsList.map((card) => <CardHelpLink key={card?.key} text={card?.text} link={card?.link} />)
  }

  return (
    <div className="payment-info-page box">
      <section className="py-3">
        <AmountReceived />
      </section>
      <IncomeReport />
      {loading ? (
        <div className="display-flex justify-content-center mt-5">
          <Loader />
        </div>
      ) : (
        <section className="content">
          {user?.banks?.[0]?.id ? <div className="help-cards">{cardsHelpText()}</div> : null}
          {user?.profileadditionalinfo?.nationality === 'BR' && !user?.banks?.[0]?.id && transactions?.length > 0 ? (
            <Callout
              icon="fa-solid fa-triangle-exclamation"
              title={t('callout.title')}
              children={<p>{t('callout.text')}</p>}
              bottomChildren={
                <Button onClick={() => navigate('/payments/data')} style={{ width: '100%' }}>
                  {t('callout.button')}
                </Button>
              }
            />
          ) : null}
          {transactions?.some((transaction) => transaction?.status === 'failed') ? (
            <Callout
              smallIconStyle
              icon="fa-solid fa-triangle-exclamation"
              title={t('callout.title')}
              children={<p>{t('callout.failed')}</p>}
              bottomChildren={
                <Button onClick={() => navigate('/payments/data')} style={{ width: '100%' }}>
                  {t('callout.buttonFailed')}
                </Button>
              }
            />
          ) : null}
          <NextPayments onNavigateTo={(path: string) => navigate(path)} title={t('title')} showCalendar={false} />
        </section>
      )}
      <AnticipationFlow transactions={transactions} onCloseFlow={() => (!!nextPage ? getToReceiveHistory(true) : null)} />
    </div>
  )
}

export default withTransaction('PaymentsInfoPageComponent', 'component')(PaymentsInfoPageComponent)
